import {useRef} from 'react';
import {cn} from '@/lib/utils';
import moment from 'moment';
import {Form, type FormProps} from '../../../app/components/Form';
import {DateField} from '../../core/components/DateField';
import styles from './BiotwinBasicsForm.module.css';
export interface BiotwinBasicsFormFieldValues {
  dateOfBirth: string;
  gender: 'male' | 'female';
  height: number;
  heightUnit: 'cm' | 'in';
  weight: number;
  weightUnit: 'kg' | 'lbs';
}
export interface BiotwinBasicsFormProps extends Omit<FormProps<BiotwinBasicsFormFieldValues>, 'fields'> {}
export const BiotwinBasicsForm = (props: BiotwinBasicsFormProps) => {
  const form = useRef<Form<BiotwinBasicsFormFieldValues>>(null);
  return (
    <Form<BiotwinBasicsFormFieldValues>
      submitButton={{
        children: 'Save',
        variant: 'warning',
      }}
      {...props}
      // @ts-expect-error - generics issue
      fields={({formState, setValue, watch}) => {
        // Once the user has tried to submit, re-check fields as values arrive so stale errors clear.
        const {isSubmitted} = formState;
        const data = watch(['height', 'heightUnit', 'weight', 'weightUnit', 'dateOfBirth']);
        const [height, heightUnit, weight, weightUnit] = data ?? [];
        const isImperialHeight = isImperial(heightUnit);
        const isImperialWeight = isImperial(weightUnit);
        const maxHeight = isImperialHeight ? maxHeightIn : maxHeightCm;
        const maxWeight = isImperialWeight ? maxWeightLb : maxWeightKg;
        const maxFtValue = Math.floor(maxHeightIn / 12);
        const maxInValue = 12;
        const ftValue = Math.floor(height / 12);
        const inValue = height % 12;
        return [
          {
            label: 'Gender',
            name: 'gender',
            rules: {
              required: true,
            },
            type: 'select-button',
            typeOptions: {
              className: 'w-full',
              options: [
                {
                  label: 'Female',
                  value: 'female',
                },
                {
                  label: 'Male',
                  value: 'male',
                },
              ],
              optionLabel: 'label',
              optionValue: 'value',
            },
          },
          {
            className: 'relative',
            label: 'Date of Birth',
            labelClassName: 'sr-only',
            name: 'dateOfBirth',
            rules: {
              validate: (value: string | null) => {
                if (value == null || value.trim() === '') {
                  return 'Date of Birth is required';
                }
                const [yearStr, monthStr, dayStr] = value.split('-');
                const year = parseInt(yearStr, 10);
                const month = monthStr ? parseInt(monthStr, 10) : null;
                const day = dayStr ? parseInt(dayStr, 10) : null;
                const dateOfBirth = new Date(year, (month ?? 1) - 1, day ?? 1);
                if (year == null) {
                  return 'Year is required';
                }
                if (month == null) {
                  return 'Month is required';
                }
                if (isNaN(dateOfBirth.getTime())) {
                  return 'Date of Birth is invalid';
                }
                if (moment().subtract(5, 'years').isBefore(dateOfBirth)) {
                  if (day == null) {
                    return 'Day is required for children under 5';
                  }
                }
                if (moment(dateOfBirth).isAfter(moment())) {
                  return 'Date of Birth cannot be in the future';
                }
                return true;
              },
            },
            type: DateField,
          },
          {
            label: 'Height',
            name: 'height',
            rules: {
              required: true,
            },
            type: 'number',
            typeOptions: {
              fractionDigits: 0,
              max: isImperialHeight ? maxFtValue : maxHeight,
              min: 0,
              onValueChange: (e) => {
                if (isImperialHeight) {
                  setValue('height', Math.max(0, Math.min(maxHeight, (e ?? 0) * 12 + inValue)), {
                    shouldValidate: isSubmitted,
                  });
                } else {
                  setValue('height', Math.max(0, Math.min(maxHeight, e ?? 0)), {shouldValidate: isSubmitted});
                }
              },
              onFocus: (e) => e.target.select(),
              required: true,
              useGrouping: false,
              value: isImperialHeight ? ftValue : height,
            },
          },
          {
            errorClassName: 'hidden',
            label: isImperialHeight ? 'in' : 'cm',
            labelClassName: 'invisible',
            name: 'height',
            rules: {
              required: isImperialHeight,
            },
            type: 'number',
            typeOptions: {
              fractionDigits: 0,
              max: isImperialHeight ? maxInValue : maxHeight,
              min: 0,
              onValueChange: (e) => {
                if (isImperialHeight) {
                  setValue('height', Math.max(0, Math.min(maxHeight, ftValue * 12 + (e ?? 0))), {
                    shouldValidate: isSubmitted,
                  });
                } else {
                  setValue('height', Math.max(0, Math.min(maxHeight, e ?? 0)), {shouldValidate: isSubmitted});
                }
              },
              onFocus: (e) => e.target.select(),
              required: true,
              useGrouping: false,
              value: isImperialHeight ? inValue : height,
            },
          },
          {
            label: 'Height Unit',
            labelStyle: {
              visibility: 'hidden',
            },
            name: 'heightUnit',
            rules: {
              required: true,
            },
            type: 'dropdown',
            typeOptions: {
              onValueChange: (e) => {
                const imperial = isImperial(e);
                if (height != null) {
                  setValue(
                    'height',
                    Math.round(
                      imperial
                        ? Math.min(convertCm2In(height), maxHeightIn)
                        : Math.min(convertIn2Cm(height), maxHeightCm),
                    ),
                  );
                }
                if (weight != null) {
                  setValue(
                    'weight',
                    Math.round(
                      imperial
                        ? Math.min(convertKg2Lb(weight), maxWeightLb)
                        : Math.min(convertLb2Kg(weight), maxWeightKg),
                    ),
                  );
                }
                setValue('heightUnit', e);
                setValue('weightUnit', imperial ? 'lbs' : 'kg');
              },
              options: [
                {
                  label: 'cm',
                  value: 'cm',
                },
                {
                  label: 'ft / in',
                  value: 'in',
                },
              ],
              optionLabel: 'label',
              optionValue: 'value',
            },
          },
          {
            label: 'Weight',
            name: 'weight',
            rules: {
              max: maxWeight,
              min: 0,
              required: true,
            },
            type: 'number',
            typeOptions: {
              fractionDigits: 0,
              max: maxWeight,
              min: 0,
              onValueChange: (e) =>
                setValue('weight', Math.max(0, Math.min(maxWeight, e ?? 0)), {shouldValidate: isSubmitted}),
              onFocus: (e) => e.target.select(),
              required: true,
              useGrouping: false,
            },
          },
          {
            label: 'Weight Unit',
            labelStyle: {
              visibility: 'hidden',
            },
            name: 'weightUnit',
            rules: {
              required: true,
            },
            type: 'dropdown',
            typeOptions: {
              onValueChange: (e) => {
                const imperial = isImperial(e);
                if (weight != null) {
                  setValue(
                    'weight',
                    Math.round(
                      imperial
                        ? Math.min(convertKg2Lb(weight), maxWeightLb)
                        : Math.min(convertLb2Kg(weight), maxWeightKg),
                    ),
                  );
                }
                if (height != null) {
                  setValue(
                    'height',
                    Math.round(
                      imperial
                        ? Math.min(convertCm2In(height), maxHeightIn)
                        : Math.min(convertIn2Cm(height), maxHeightCm),
                    ),
                  );
                }
                setValue('heightUnit', imperial ? 'in' : 'cm');
                setValue('weightUnit', e);
              },
              options: [
                {
                  label: 'kg',
                  value: 'kg',
                },
                {
                  label: 'lbs',
                  value: 'lbs',
                },
              ],
              optionLabel: 'label',
              optionValue: 'value',
            },
          },
        ];
      }}
      ref={form}
      render={({formState, renderError, renderFieldByName, renderSubmitButton}) => {
        const dateOfBirth = form.current?.getValues('dateOfBirth');
        const heightUnit = form.current?.getValues('heightUnit');
        const under5YearsOld = dateOfBirth ? moment().subtract(5, 'years').isBefore(moment(dateOfBirth)) : false;
        return (
          <div className={'flex flex-col gap-4'}>
            {renderError()}
            <div>
              <div className="mb-2">
                <div className="text-sm font-medium">Date of Birth</div>
                {under5YearsOld && (
                  <div className="text-muted-foreground text-sm">* Day is required for children under 5.</div>
                )}
              </div>
              {renderFieldByName('dateOfBirth')}
            </div>
            <hr className="my-2" />
            {renderFieldByName('gender')}
            <div className={styles.row}>
              <div className={styles.rowInputTextContainer}>
                {heightUnit != null && isImperial(heightUnit) && (
                  <span className="absolute top-0 mt-10 left-4 text-muted-foreground">ft</span>
                )}
                {renderFieldByName('height')}
              </div>
              {heightUnit != null && isImperial(heightUnit) && (
                <div className={styles.rowInputTextContainer}>
                  <span className="absolute top-0 mt-10 left-4 text-muted-foreground">in</span>
                  {renderFieldByName('height', 1)}
                </div>
              )}
              <div className={styles.rowDropdownContainer}>{renderFieldByName('heightUnit')}</div>
            </div>
            <div
              className={cn(styles.row, {
                [styles.error]: !!formState.errors.weight,
              })}
            >
              <div className={styles.rowInputTextContainer}>{renderFieldByName('weight')}</div>
              <div className={styles.rowDropdownContainer}>{renderFieldByName('weightUnit')}</div>
            </div>
            {renderSubmitButton()}
          </div>
        );
      }}
    />
  );
};
const maxHeightCm = 250;
const maxHeightIn = Math.round(convertCm2In(maxHeightCm));
const maxWeightKg = 300;
const maxWeightLb = Math.round(convertKg2Lb(maxWeightKg));
function isImperial(unitType: string) {
  return unitType === 'in' || unitType === 'lbs';
}
function convertCm2In(value: number) {
  return value / 2.54;
}
function convertIn2Cm(value: number) {
  return value * 2.54;
}
function convertKg2Lb(value: number) {
  return value * 2.205;
}
function convertLb2Kg(value: number) {
  return value / 2.205;
}
